// Which account this browser tab is looking at, and what the person may do inside it.
//
// The account id is the 20-character public one from the URL —
// /accounts/orWQlK0M8BCmMmkqu3cc/contacts — never the integer tenant id. The server never
// publishes that integer, so the client cannot leak it.
//
// WHY THE ID LIVES IN THE URL AND NOT IN STATE. A teammate on two accounts opens one in
// each tab. State in a provider is per-tab and would work; state in localStorage is
// shared and would not — switching account in one tab would silently move the other. The
// URL is the only place that is both per-tab and shareable, which is what makes a pasted
// link land somebody in the account the sender meant.
import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { apiFetch, setAccountHeader } from "@/lib/api-client";

/**
 * WhatsApp calling for this seat in this account, as GET /api/team/me reports it
 * (docs/vault/Plans/WhatsApp-Calling.md §5.2 X1). It gates the call dock and the Calls link
 * with no extra request; the server still decides every call action itself.
 */
export interface CallingFlags {
  /** The platform switch is on AND this account is on the pilot allowlist. */
  platform: boolean;
  /** `platform`, and at least one of this account's numbers takes calls. */
  enabled: boolean;
  /** A real seat. A platform operator acting inside an account has none, so cannot answer. */
  seat: boolean;
}

/** What a server that predates calling, or an identity not loaded yet, means: nothing is on. */
export const CALLING_OFF: CallingFlags = { platform: false, enabled: false, seat: false };

/** Reads the `calling` block strictly: anything but a literal `true` is off. */
export function readCalling(raw: unknown): CallingFlags {
  if (typeof raw !== "object" || raw === null) return CALLING_OFF;
  const flags = raw as Record<string, unknown>;
  return { platform: flags.platform === true, enabled: flags.enabled === true, seat: flags.seat === true };
}

/** One account this person can act in. Mirrors the `accounts` array from /api/team/me. */
export interface AccountSummary {
  account_id: string;
  name: string | null;
  role: string;
  role_label: string;
  is_own: boolean;
}

export interface Identity {
  /** `name` is the full name; first/last are what the person typed, null until they do. */
  user: {
    id: number;
    email: string;
    name: string | null;
    first_name?: string | null;
    last_name?: string | null;
  };
  account_id: string;
  role: string;
  role_label: string;
  is_owner: boolean;
  /** The EFFECTIVE set — role preset plus per-seat overrides — computed server-side. */
  permissions: string[];
  /** The account being looked at. Null for a workspace row written before it had a name. */
  account_name: string | null;
  /**
   * HOW MANY accounts this person holds, not which. The list moved to
   * GET /api/team/accounts, fetched when somebody opens the switcher — identity is read
   * on every page load and answers about ONE account, so shipping a directory with it
   * made every login pay for however many sub-accounts the person had been invited to.
   * Below two there is nothing to switch, which is all this number is for.
   */
  account_count: number;
  /** Absent from a server that predates calling; read through `readCalling`. */
  calling?: CallingFlags;
}

interface AccountContextValue {
  /** From the URL, or whatever the server resolved. Null until identity loads. */
  accountId: string | null;
  identity: Identity | null;
  isLoading: boolean;
  /** Prefix an in-app path. `/contacts` -> `/accounts/<id>/contacts`. */
  path: (to: string) => string;
  can: (permission: string) => boolean;
  /** WhatsApp calling here. All false until identity loads, like `can`. */
  calling: CallingFlags;
}

const AccountContext = createContext<AccountContextValue>({
  accountId: null,
  identity: null,
  isLoading: true,
  path: (to) => to,
  can: () => false,
  calling: CALLING_OFF,
});

export const identityKey = ["team", "me"] as const;

export function AccountProvider({ children }: { children: ReactNode }) {
  const { accountId = null } = useParams<{ accountId: string }>();

  // Set BEFORE the query runs, not in an effect after it. An effect fires after render,
  // so the first request of a freshly-loaded page would go out with the previous
  // account's header — or none — and come back with the wrong account's data.
  setAccountHeader(accountId);

  const { data, isLoading } = useQuery({
    queryKey: [...identityKey, accountId],
    queryFn: () => apiFetch<Identity>("/api/team/me"),
    // Permissions decide what renders, so a stale answer shows somebody controls they no
    // longer hold. Short, not zero: this runs on every navigation.
    staleTime: 30_000,
    retry: false,
  });

  useEffect(() => {
    // Keep the header pinned to what the server actually resolved. If the URL named an
    // account this person cannot open, the server fell back to their own and said so in
    // `account_id`; following that keeps every later request consistent with the data
    // already on screen, instead of re-asking for an account that will keep falling back.
    if (data?.account_id) setAccountHeader(data.account_id);
  }, [data?.account_id]);

  const value = useMemo<AccountContextValue>(() => {
    const active = data?.account_id ?? accountId;
    const granted = new Set(data?.permissions ?? []);
    return {
      accountId: active,
      identity: data ?? null,
      isLoading,
      path: (to: string) => {
        if (!active) return to;
        // Already account-scoped, or an external link: leave it alone.
        if (to.startsWith("/accounts/") || /^[a-z]+:/i.test(to)) return to;
        return `/accounts/${active}${to.startsWith("/") ? "" : "/"}${to}`;
      },
      // Closed by default. An identity that has not loaded grants nothing, so a slow
      // network renders a reduced UI rather than flashing controls that then vanish.
      can: (permission: string) => granted.has(permission),
      calling: readCalling(data?.calling),
    };
  }, [data, accountId, isLoading]);

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount(): AccountContextValue {
  return useContext(AccountContext);
}

/** Just the prefixer, for the many components that only need to build a link. */
export function useAccountPath(): (to: string) => string {
  return useContext(AccountContext).path;
}
